import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["timer", "status", "startButton", "stopButton", "transcript", "remoteAudio", "evaluation"]
  static values = {
    tokenUrl: String,
    turnUrl: String,
    finishUrl: String,
    voiceModelStorageKey: { type: String, default: "" },
    maxSeconds: { type: Number, default: 300 }
  }

  connect() {
    this.peerConnection = null
    this.dataChannel = null
    this.mediaStream = null
    this.audioContext = null
    this.audioSource = null
    this.audioProcessor = null
    this.geminiSocket = null
    this.glmSubscription = null
    this.timerId = null
    this.remainingSeconds = this.maxSecondsValue
    this.startedAt = null
    this.savedEventKeys = new Set()
    this.finishRequested = false
    this.playbackChain = Promise.resolve()
    this.geminiUserTranscript = ""
    this.geminiAssistantTranscript = ""
    this.renderTimer()
  }

  disconnect() {
    this.cleanupConnection()
  }

  async start() {
    if (this.isConnectedToRealtime() || this.finishRequested) return

    this.setStatus("마이크 권한을 확인하고 있습니다.")
    this.setControls({ active: true })

    try {
      const tokenPayload = await this.fetchClientSecret()
      const provider = tokenPayload.provider || "openai"

      if (provider === "gemini") {
        await this.startGemini(tokenPayload)
      } else if (provider === "glm") {
        await this.startGlm(tokenPayload)
      } else {
        await this.startOpenai(tokenPayload)
      }

      this.startedAt = Date.now()
      this.startTimer()
      this.clearEmptyTranscript()
      this.setStatus("AI 코치와 연결되었습니다. 자연스럽게 영어로 답변하세요.")
    } catch (error) {
      this.cleanupConnection()
      this.setControls({ active: false })
      this.setStatus(this.errorMessage(error))
    }
  }

  async startOpenai(tokenPayload) {
    const token = tokenPayload.value || tokenPayload.client_secret?.value
    if (!token) throw new Error("Realtime client secret is missing.")

    this.peerConnection = new RTCPeerConnection()
    this.peerConnection.ontrack = (event) => {
      if (this.hasRemoteAudioTarget) this.remoteAudioTarget.srcObject = event.streams[0]
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    this.mediaStream.getAudioTracks().forEach((track) => this.peerConnection.addTrack(track, this.mediaStream))

    this.dataChannel = this.peerConnection.createDataChannel("oai-events")
    this.dataChannel.addEventListener("open", () => this.requestFirstQuestion())
    this.dataChannel.addEventListener("message", (event) => this.handleOpenaiEvent(event))
    this.dataChannel.addEventListener("error", () => this.setStatus("Realtime 이벤트 채널 오류가 발생했습니다."))

    const offer = await this.peerConnection.createOffer()
    await this.peerConnection.setLocalDescription(offer)

    const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
      method: "POST",
      body: offer.sdp,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/sdp"
      }
    })

    if (!sdpResponse.ok) throw new Error(await sdpResponse.text())

    await this.peerConnection.setRemoteDescription({
      type: "answer",
      sdp: await sdpResponse.text()
    })
  }

  async startGemini(tokenPayload) {
    const token = tokenPayload.value || tokenPayload.client_secret?.value
    const endpoint = tokenPayload.metadata?.endpoint
    if (!token || !endpoint) throw new Error("Gemini Live 세션 정보가 없습니다.")

    this.geminiSocket = new WebSocket(`${endpoint}?access_token=${encodeURIComponent(token)}`)
    this.geminiSocket.addEventListener("message", (event) => this.handleGeminiEvent(event))
    this.geminiSocket.addEventListener("error", () => this.setStatus("Gemini Live 연결 오류가 발생했습니다."))
    this.geminiSocket.addEventListener("close", () => {
      if (!this.finishRequested) this.setStatus("Gemini Live 연결이 종료되었습니다.")
    })

    await new Promise((resolve, reject) => {
      this.geminiSocket.addEventListener("open", resolve, { once: true })
      this.geminiSocket.addEventListener("error", () => reject(new Error("Gemini Live 연결에 실패했습니다.")), { once: true })
    })

    this.geminiSocket.send(JSON.stringify({ setup: tokenPayload.session }))
    await this.startPcmCapture((audio) => {
      this.sendGeminiEvent({
        realtimeInput: {
          mediaChunks: [
            {
              mimeType: "audio/pcm;rate=16000",
              data: audio
            }
          ]
        }
      })
    })
  }

  async startGlm(tokenPayload) {
    if (!window.ActionCableConsumer) throw new Error("Realtime relay를 시작할 수 없습니다.")

    await new Promise((resolve, reject) => {
      this.glmSubscription = window.ActionCableConsumer.subscriptions.create(
        { channel: "EnglishSpeakingRealtimeChannel" },
        {
          connected: () => {
            this.glmSubscription.perform("start", {
              session_id: tokenPayload.relay?.session_id,
              model: tokenPayload.model,
              voice: tokenPayload.voice,
              session: tokenPayload.session
            })
          },
          received: (message) => {
            if (message.error) {
              reject(new Error(message.error))
              return
            }
            if (message.type === "relay.connected") {
              resolve()
              this.requestFirstQuestionForGlm()
              return
            }
            this.handleGlmMessage(message)
          },
          rejected: () => reject(new Error("GLM relay 구독이 거절되었습니다."))
        }
      )
    })

    await this.startPcmCapture((audio) => {
      this.glmSubscription?.perform("append_audio", { audio })
    })
  }

  async stop() {
    if (this.finishRequested) return

    this.finishRequested = true
    const durationSeconds = this.elapsedSeconds()
    this.setStatus("대화 내용을 평가하고 있습니다.")
    this.setControls({ active: false, finishing: true })
    this.cleanupConnection()

    try {
      const response = await this.postJson(this.finishUrlValue, { duration_seconds: durationSeconds })
      if (!response.ok) {
        const payload = await this.safeJson(response)
        throw new Error(payload.error || "스피킹 평가 생성에 실패했습니다.")
      }

      const payload = await response.json()
      if (payload.redirect_url) {
        window.location.href = payload.redirect_url
      } else {
        window.location.reload()
      }
    } catch (error) {
      this.finishRequested = false
      this.setControls({ active: false })
      this.setStatus(this.errorMessage(error))
    }
  }

  async fetchClientSecret() {
    const body = {}
    const voiceModel = this.selectedVoiceModel()
    const provider = this.selectedVoiceProvider()
    if (voiceModel) body.model = voiceModel
    if (provider) body.provider = provider

    const response = await this.postJson(this.tokenUrlValue, body)
    const payload = await this.safeJson(response)
    if (!response.ok) throw new Error(payload.error || "Realtime 세션을 만들 수 없습니다.")
    return payload
  }

  selectedVoiceModel() {
    if (!this.voiceModelStorageKeyValue) return ""

    try {
      return localStorage.getItem(this.voiceModelStorageKeyValue) || ""
    } catch (_error) {
      return ""
    }
  }

  selectedVoiceProvider() {
    if (!this.voiceModelStorageKeyValue) return ""

    try {
      return localStorage.getItem(this.voiceModelStorageKeyValue.replace(/-model$/, "-provider")) || ""
    } catch (_error) {
      return ""
    }
  }

  requestFirstQuestion() {
    this.sendRealtimeEvent({
      type: "response.create",
      response: {
        instructions: "Greet the learner briefly, then ask the first short question for this speaking practice. Keep the conversation in English."
      }
    })
  }

  requestFirstQuestionForGemini() {
    this.sendGeminiEvent({
      clientContent: {
        turns: [
          {
            role: "user",
            parts: [
              {
                text: "Greet me briefly, then ask the first short question for this English speaking practice."
              }
            ]
          }
        ],
        turnComplete: true
      }
    })
  }

  requestFirstQuestionForGlm() {
    this.glmSubscription?.perform("send_event", {
      event: {
        event_id: this.randomId(),
        type: "response.create",
        response: {
          instructions: "Greet the learner briefly, then ask the first short question for this speaking practice. Keep the conversation in English.",
          modalities: ["text", "audio"]
        }
      }
    })
  }

  handleOpenaiEvent(event) {
    let payload
    try {
      payload = JSON.parse(event.data)
    } catch (_error) {
      return
    }

    if (payload.type === "error") {
      this.setStatus(payload.error?.message || "Realtime 오류가 발생했습니다.")
      return
    }

    if (payload.type === "conversation.item.input_audio_transcription.completed") {
      this.saveTurnFromEvent("user", payload.transcript, payload)
      return
    }

    if (payload.type === "response.output_audio_transcript.done") {
      this.saveTurnFromEvent("assistant", payload.transcript, payload)
      return
    }

    if (payload.type === "response.output_text.done") {
      this.saveTurnFromEvent("assistant", payload.text, payload)
      return
    }

    if (payload.type === "response.done") this.setStatus("답변을 듣고 이어서 말해보세요.")
  }

  handleGeminiEvent(event) {
    let payload
    try {
      payload = JSON.parse(event.data)
    } catch (_error) {
      return
    }

    if (payload.setupComplete) {
      this.requestFirstQuestionForGemini()
      return
    }

    const inputText = payload.inputTranscription?.text || payload.input_transcription?.text
    if (inputText) this.geminiUserTranscript += inputText

    const outputText = payload.outputTranscription?.text || payload.output_transcription?.text
    if (outputText) this.geminiAssistantTranscript += outputText

    const serverContent = payload.serverContent || payload.server_content
    const parts = serverContent?.modelTurn?.parts || serverContent?.model_turn?.parts || []
    parts.forEach((part) => {
      if (part.text) this.geminiAssistantTranscript += part.text
      const inlineData = part.inlineData || part.inline_data
      if (inlineData?.data) this.playAudioDelta(inlineData.data, this.sampleRateFromMime(inlineData.mimeType || inlineData.mime_type) || 24000)
    })

    if (serverContent?.turnComplete || serverContent?.turn_complete) {
      this.flushBufferedTurn("user", this.geminiUserTranscript, { type: "gemini.input_transcription.completed" })
      this.flushBufferedTurn("assistant", this.geminiAssistantTranscript, { type: "gemini.output_transcription.completed" })
      this.geminiUserTranscript = ""
      this.geminiAssistantTranscript = ""
      this.setStatus("답변을 듣고 이어서 말해보세요.")
    }
  }

  handleGlmMessage(message) {
    const payload = message.event || message
    if (payload.type === "error") {
      this.setStatus(payload.error?.message || "GLM Realtime 오류가 발생했습니다.")
      return
    }

    const type = payload.type || ""
    if (type.includes("input_audio") && type.includes("transcription") && payload.transcript) {
      this.saveTurnFromEvent("user", payload.transcript, payload)
      return
    }

    if (type.includes("audio") && type.includes("delta") && payload.delta) {
      this.playAudioDelta(payload.delta, 24000)
    }

    if (type.includes("text") && type.includes("delta") && payload.delta) {
      this.glmAssistantTranscript = `${this.glmAssistantTranscript || ""}${payload.delta}`
    }

    if ((type.includes("transcript") || type.includes("text")) && type.includes("done")) {
      this.flushBufferedTurn("assistant", this.glmAssistantTranscript || payload.transcript || payload.text, payload)
      this.glmAssistantTranscript = ""
    }

    if (type === "response.done") this.setStatus("답변을 듣고 이어서 말해보세요.")
  }

  flushBufferedTurn(role, content, payload) {
    const text = (content || "").trim()
    if (!text) return
    this.saveTurnFromEvent(role, text, payload)
  }

  async saveTurnFromEvent(role, content, payload) {
    const text = (content || "").trim()
    if (!text) return

    const key = payload.event_id || `${payload.type}:${payload.item_id || ""}:${payload.output_index || ""}:${text}`
    if (this.savedEventKeys.has(key)) return
    this.savedEventKeys.add(key)

    this.appendTurn(role, text)

    try {
      await this.postJson(this.turnUrlValue, {
        role,
        content: text,
        metadata: {
          event_id: payload.event_id,
          event_type: payload.type,
          item_id: payload.item_id,
          response_id: payload.response_id
        }
      })
    } catch (_error) {
      this.setStatus("전사 저장 중 네트워크 오류가 발생했습니다. 대화는 계속할 수 있습니다.")
    }
  }

  appendTurn(role, content) {
    this.clearEmptyTranscript()

    const article = document.createElement("article")
    article.className = `es-turn ${role}`

    const label = document.createElement("span")
    label.className = "es-turn__role"
    label.textContent = role === "user" ? "You" : "AI Coach"

    const body = document.createElement("p")
    body.className = "es-turn__content"
    body.textContent = content

    article.append(label, body)
    this.transcriptTarget.appendChild(article)
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  async startPcmCapture(onChunk) {
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    })
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    this.audioContext = new AudioContextClass()
    this.audioSource = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.audioProcessor = this.audioContext.createScriptProcessor(4096, 1, 1)
    this.audioProcessor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0)
      onChunk(this.floatToPcm16Base64(input, this.audioContext.sampleRate, 16000))
    }
    this.audioSource.connect(this.audioProcessor)
    this.audioProcessor.connect(this.audioContext.destination)
  }

  floatToPcm16Base64(float32, inputRate, outputRate) {
    const ratio = inputRate / outputRate
    const outputLength = Math.floor(float32.length / ratio)
    const buffer = new ArrayBuffer(outputLength * 2)
    const view = new DataView(buffer)
    for (let i = 0; i < outputLength; i += 1) {
      const sample = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)] || 0))
      view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
    }
    return this.arrayBufferToBase64(buffer)
  }

  playAudioDelta(base64Audio, sampleRate = 24000) {
    const wav = this.pcm16Base64ToWav(base64Audio, sampleRate)
    const url = URL.createObjectURL(new Blob([wav], { type: "audio/wav" }))
    this.playbackChain = this.playbackChain
      .catch(() => {})
      .then(() => new Promise((resolve) => {
        const audio = new Audio(url)
        audio.onended = () => {
          URL.revokeObjectURL(url)
          resolve()
        }
        audio.onerror = () => {
          URL.revokeObjectURL(url)
          resolve()
        }
        audio.play().catch(resolve)
      }))
  }

  pcm16Base64ToWav(base64Audio, sampleRate) {
    const pcm = this.base64ToArrayBuffer(base64Audio)
    const header = new ArrayBuffer(44)
    const view = new DataView(header)
    this.writeAscii(view, 0, "RIFF")
    view.setUint32(4, 36 + pcm.byteLength, true)
    this.writeAscii(view, 8, "WAVE")
    this.writeAscii(view, 12, "fmt ")
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, 1, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * 2, true)
    view.setUint16(32, 2, true)
    view.setUint16(34, 16, true)
    this.writeAscii(view, 36, "data")
    view.setUint32(40, pcm.byteLength, true)
    const wav = new Uint8Array(44 + pcm.byteLength)
    wav.set(new Uint8Array(header), 0)
    wav.set(new Uint8Array(pcm), 44)
    return wav
  }

  sampleRateFromMime(mimeType) {
    const match = mimeType?.match(/rate=(\d+)/)
    return match ? Number.parseInt(match[1], 10) : null
  }

  writeAscii(view, offset, value) {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i))
  }

  randomId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return `evt_${Date.now()}_${Math.random().toString(16).slice(2)}`
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
    return btoa(binary)
  }

  base64ToArrayBuffer(base64) {
    const binary = atob(base64)
    const buffer = new ArrayBuffer(binary.length)
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
    return buffer
  }

  clearEmptyTranscript() {
    if (!this.hasTranscriptTarget) return
    this.transcriptTarget.querySelectorAll(".es-room__empty").forEach((node) => node.remove())
  }

  sendRealtimeEvent(payload) {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    this.dataChannel.send(JSON.stringify(payload))
  }

  sendGeminiEvent(payload) {
    if (!this.geminiSocket || this.geminiSocket.readyState !== WebSocket.OPEN) return
    this.geminiSocket.send(JSON.stringify(payload))
  }

  startTimer() {
    clearInterval(this.timerId)
    this.remainingSeconds = this.maxSecondsValue
    this.renderTimer()
    this.timerId = setInterval(() => {
      this.remainingSeconds = Math.max(this.remainingSeconds - 1, 0)
      this.renderTimer()
      if (this.remainingSeconds === 0) this.stop()
    }, 1000)
  }

  renderTimer() {
    if (!this.hasTimerTarget) return
    const minutes = Math.floor(this.remainingSeconds / 60).toString().padStart(2, "0")
    const seconds = (this.remainingSeconds % 60).toString().padStart(2, "0")
    this.timerTarget.textContent = `${minutes}:${seconds}`
  }

  elapsedSeconds() {
    if (!this.startedAt) return 0
    return Math.max(Math.round((Date.now() - this.startedAt) / 1000), 0)
  }

  isConnectedToRealtime() {
    return Boolean(this.peerConnection || this.geminiSocket || this.glmSubscription)
  }

  cleanupConnection() {
    clearInterval(this.timerId)
    this.timerId = null

    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }

    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }

    if (this.geminiSocket) {
      this.geminiSocket.close()
      this.geminiSocket = null
    }

    if (this.glmSubscription) {
      this.glmSubscription.perform("stop", {})
      window.ActionCableConsumer?.subscriptions?.remove(this.glmSubscription)
      this.glmSubscription = null
    }

    if (this.audioProcessor) {
      this.audioProcessor.disconnect()
      this.audioProcessor = null
    }

    if (this.audioSource) {
      this.audioSource.disconnect()
      this.audioSource = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }
  }

  setControls({ active = false, finishing = false } = {}) {
    if (this.hasStartButtonTarget) this.startButtonTarget.disabled = active || finishing
    if (this.hasStopButtonTarget) this.stopButtonTarget.disabled = !active || finishing
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    })
  }

  async safeJson(response) {
    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  errorMessage(error) {
    const message = error?.message || ""
    if (message.includes("Permission denied") || message.includes("NotAllowedError")) {
      return "마이크 권한이 필요합니다. 브라우저 권한을 허용한 뒤 다시 시작하세요."
    }
    if (message.includes("OpenAI") || message.includes("Gemini") || message.includes("GLM") || message.includes("Realtime")) return message
    return "스피킹 세션을 시작할 수 없습니다. 잠시 후 다시 시도해주세요."
  }
}
